import React from 'react';
import { Star, SealCheck, Clock, Warning } from '@phosphor-icons/react';
import { cn } from '../../lib/utils';
/**
 * Review status semantics (post Faza 1.5 cleanup):
 *
 *  • 'transactional'        — Review written by a logged-in platform user
 *                              who completed a transaction with the subject.
 *                              The platform itself is the verification. No
 *                              per-review badge; trust comes from the
 *                              reviewer's profile verification level.
 *  • 'verified_by_document' — Review from an off-platform reviewer who
 *                              attached a contract (e.g. C168) which we
 *                              validated. Reserved mostly for the asymmetric
 *                              case where a tenant invites an old landlord
 *                              outside the platform.
 *  • 'pending'              — Off-platform review awaiting document validation
 *                              by our team. NEVER shown on public profiles —
 *                              owner-dashboard only.
 *  • 'unverified'           — Legacy / edge case. Owner-dashboard only.
 *  • 'verified_by_otp'      — DEPRECATED. Old email-OTP path. Treated as
 *                              'transactional' on display (no badge).
 */
export type ReviewStatus = 'transactional' | 'verified_by_document' | 'pending' | 'unverified' | 'verified_by_otp'; // legacy, treated as transactional
export interface Review {
  id: string;
  reviewerName: string;
  /**
   * Free-text role label. A landlord/agent reviewing a tenant gives
   * 'Proprietar' / 'Agent'. A tenant reviewing supply gives 'Chiriaș'.
   */
  reviewerRole: 'Proprietar' | 'Agent' | 'Chiriaș' | string;
  /** Slug of the reviewer's profile, when they have one. Drives clickable name. */
  reviewerSlug?: string;
  /**
   * Whether the reviewer has FULLY completed their profile verifications
   * (email + ID + business/proprietate for supply; email + ID for tenants).
   * Drives the small SealCheck reward badge next to their name.
   */
  reviewerVerified?: boolean;
  rating: number;
  categoryRatings: Record<string, number>;
  date: string;
  body: string;
  periodLabel: string;
  status: ReviewStatus;
  avatar?: string;
}
interface ReviewCardProps {
  review: Review;
  onReviewerClick?: (slug: string) => void;
}
/**
 * Reviews that are safe to surface on a PUBLIC profile. Filters out
 * `pending` and `unverified`, plus anything we don't recognise. Use this
 * helper everywhere a non-owner viewer renders someone's reviews so the
 * aggregate rating and the visible cards stay in sync.
 */
export function getPublicReviews(reviews: Review[]): Review[] {
  return reviews.filter(r => r.status === 'transactional' || r.status === 'verified_by_document' || r.status === 'verified_by_otp');
}
/**
 * Compact summary pill used in the reviews section header on profile pages.
 * Renders "★ 4.7 · 23 recenzii". Returns null when there are no reviews so
 * the parent can render its own empty-state copy.
 */
export function ReviewsRatingSummary({
  reviews,
  className
}: {
  reviews: Pick<Review, 'rating'>[];
  className?: string;
}) {
  if (!reviews || reviews.length === 0) return null;
  const avg = reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / reviews.length;
  return <span className={cn('inline-flex items-center gap-1.5 text-xs', className)} aria-label={`Rating mediu ${avg.toFixed(1)} din 5 din ${reviews.length} ${reviews.length === 1 ? 'recenzie' : 'recenzii'}`} data-id="element-947">
      <Star size={14} weight="fill" className="text-lime-1" data-id="element-948" />
      <span className="font-medium text-light-1" data-id="element-949">{avg.toFixed(1)}</span>
      <span className="text-light-3" data-id="element-950">
        · {reviews.length} {reviews.length === 1 ? 'recenzie' : 'recenzii'}
      </span>
    </span>;
}
export function ReviewCard({
  review,
  onReviewerClick
}: ReviewCardProps) {
  const nameIsClickable = !!(review.reviewerSlug && onReviewerClick);
  const handleNameClick = () => {
    if (nameIsClickable && review.reviewerSlug) {
      onReviewerClick(review.reviewerSlug);
    }
  };
  return <div className="bg-dark-2 border border-dark-3 rounded-2xl px-6 py-5" data-id="element-951">
      {/* Header row */}
      <div className="flex items-start justify-between mb-4 gap-4" data-id="element-952">
        <div className="flex items-center gap-3 min-w-0" data-id="element-953">
          {review.avatar ? <img src={review.avatar} alt={review.reviewerName} className="w-10 h-10 rounded-full object-cover shrink-0" data-id="element-954" /> : <div className="w-10 h-10 rounded-full bg-dark-3 flex items-center justify-center text-sm font-medium text-light-2 shrink-0" data-id="element-955">
              {review.reviewerName[0]}
            </div>}
          <div className="min-w-0" data-id="element-956">
            <div className="font-medium text-light-1 flex items-center gap-1.5 min-w-0" data-id="element-957">
              {nameIsClickable ? <button type="button" onClick={handleNameClick} className="truncate hover:underline underline-offset-4 cursor-pointer text-left" data-id="element-958">
                  {review.reviewerName}
                </button> : <span className="truncate" data-id="element-959">{review.reviewerName}</span>}
              {review.reviewerVerified && <SealCheck size={14} weight="fill" className="text-lime-1 shrink-0" aria-label="Profil verificat" data-id="element-960" />}
            </div>
            <div className="text-xs text-light-3 truncate" data-id="element-961">
              {review.reviewerRole} · {review.periodLabel}
            </div>
          </div>
        </div>
        <div className="text-right shrink-0" data-id="element-962">
          <div className="flex items-center gap-1 text-lime-1 mb-1" data-id="element-963">
            {Array.from({
            length: Math.max(0, Math.min(5, Math.round(review.rating || 0)))
          }).map((_, i) => <Star key={i} size={14} weight="fill" data-id="element-964" />)}
          </div>
          <div className="text-xs text-light-4" data-id="element-965">{review.date}</div>
        </div>
      </div>

      {/* Body */}
      <p className="text-sm text-light-2 leading-relaxed mb-4" data-id="element-966">
        "{review.body}"
      </p>

      {/* Footer status pill — only shown for non-implicit states */}
      {(review.status === 'verified_by_document' || review.status === 'pending' || review.status === 'unverified') && <div className="flex justify-end" data-id="element-967">
          {review.status === 'verified_by_document' && <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-lime-5 border border-lime-4 text-xs font-medium text-lime-1" data-id="element-968">
              <SealCheck size={14} weight="fill" data-id="element-969" />
              Verificat cu contract
            </div>}
          {review.status === 'pending' && <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-dark-3 border border-dark-4 text-xs font-medium text-light-3" data-id="element-970">
              <Clock size={14} weight="regular" data-id="element-971" />
              Așteaptă verificare
            </div>}
          {review.status === 'unverified' && <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-dark-3 border border-dark-4 text-xs font-medium text-light-3" data-id="element-972">
              <Warning size={14} weight="regular" data-id="element-973" />
              Neverificat
            </div>}
        </div>}
    </div>;
}